import React from "react";
import { Collapse, DatePicker, Select, TimePicker, InputNumber } from "antd";
import dayjs from "dayjs";

const { Panel } = Collapse;
const { RangePicker: DateRange } = DatePicker;
const { RangePicker: TimeRange } = TimePicker;

export interface AseoFilterValues {
  fechaInicio: [any, any];
  aseadores: string[];
  propiedad: string[];
  sector: string[];
  horario: [any, any];
  sueldoAseador: [number | null, number | null];
  costoLimpieza: [number | null, number | null];
  urgencia: string[];
  estado: string[];
  // `rolAF` eliminado en round 2026-05-13 — el modelo Cleaning ya no
  // tiene esa columna.
  observaciones: string;
}

export const initialAseoFilterValues: AseoFilterValues = {
  fechaInicio: [null, null],
  aseadores: [],
  propiedad: [],
  sector: [],
  horario: [null, null],
  sueldoAseador: [null, null],
  costoLimpieza: [null, null],
  urgencia: [],
  estado: [],
  observaciones: "",
};

// Formato/parseo CLP compartido por los rangos numéricos (mín-máx): muestra
// "$ 40.000" con separador de miles es-CL en AMBOS extremos (antes el "Máx"
// usaba un formato distinto sin separador → se veía inconsistente). Al vaciar
// el campo, AntD dispara onChange(null) sin pasar por el parser, así que el
// reset a "vacío" sigue funcionando.
const clpFormatter = (v?: number | string): string =>
  v === undefined || v === null || v === ""
    ? ""
    : `$ ${Number(v).toLocaleString("es-CL")}`;
const clpParser = (s?: string): number =>
  s ? Number(String(s).replace(/[^\d]/g, "")) : (0 as number);

interface AseoFilterBarProps {
  filterValues: AseoFilterValues;
  setFilterValues: React.Dispatch<React.SetStateAction<AseoFilterValues>>;
  // Apertura controlada del acordeón: permite abrirlo programáticamente.
  // Opcionales para no romper otros usos del componente.
  panelOpen?: boolean;
  setPanelOpen?: (open: boolean) => void;
  // El filtro "Costo limpieza" solo tiene sentido para admin: al aseador el
  // backend le borra costo_limpieza (dato financiero AF/cliente) y el mapper
  // lo deja en 0, así que cualquier mínimo > 0 vaciaba sus 3 tablas sin
  // explicación (auditoría 2026-07-13). Default true para no romper otros usos.
  showCostoLimpieza?: boolean;
}

/**
 * Panel "Filtros avanzados" (acordeón colapsable). Desde round 2026-07-14 los
 * filtros más usados (notas, mes, aseador, propiedad, sector, estado) viven en
 * la barra rápida siempre visible (QuickAseoFilters); este panel conserva SOLO
 * los menos frecuentes: rango exacto de fechas, horario, montos y urgencia.
 * Ambos comparten el mismo estado `filterValues`, así que no hay duplicación de
 * lógica ni de filtrado.
 */
const AseoFilterBar: React.FC<AseoFilterBarProps> = ({
  filterValues,
  setFilterValues,
  panelOpen,
  setPanelOpen,
  showCostoLimpieza = true,
}) => {
  return (
    <Collapse
      bordered={false}
      className="advanced-filter-collapse"
      expandIconPosition="end"
      // Si el padre pasa panelOpen lo usamos como fuente de verdad (controlado);
      // si no, cae al comportamiento previo (colapsado/no controlado).
      activeKey={panelOpen === undefined ? undefined : panelOpen ? ["1"] : []}
      onChange={(keys) => setPanelOpen?.((keys as string[]).length > 0)}
    >
      <Panel
        header={
          <div className="advanced-filter-header">
            <span>Filtros avanzados</span>
            <span className="advanced-filter-subtitle">
              Rango exacto de fechas, horario, montos y urgencia
            </span>
          </div>
        }
        key="1"
      >
        <div className="advanced-filter-content">
          {/* Fecha del aseo (rango exacto) */}
          <div className="af-field af-field--wide">
            <label className="af-label">Fecha del aseo (rango)</label>
            <DateRange
              // value enlazado para que "Limpiar" / quitar el chip resetee
              // visualmente el picker (antes quedaba con la fecha vieja porque
              // era no-controlado).
              value={filterValues.fechaInicio as any}
              onChange={(dates) =>
                setFilterValues((f) => ({
                  ...f,
                  fechaInicio: dates ? dates : [null, null],
                }))
              }
              format="DD-MM-YYYY"
              allowClear
              placeholder={["Desde", "Hasta"]}
            />
          </div>

          {/* Horario */}
          <div className="af-field af-field--wide">
            <label className="af-label">Horario</label>
            <TimeRange
              format="HH:mm"
              showSecond={false}
              allowClear
              // El filtro guarda strings "HH:mm"; reconstruimos dayjs solo para
              // el value del picker (así "Limpiar"/quitar chip lo resetea).
              value={
                filterValues.horario[0] && filterValues.horario[1]
                  ? ([
                      dayjs(filterValues.horario[0] as string, "HH:mm"),
                      dayjs(filterValues.horario[1] as string, "HH:mm"),
                    ] as any)
                  : null
              }
              onChange={(times) =>
                setFilterValues((f) => ({
                  ...f,
                  horario: times
                    ? [times[0]?.format("HH:mm"), times[1]?.format("HH:mm")]
                    : [null, null],
                }))
              }
              placeholder={["Desde", "Hasta"]}
            />
          </div>

          {/* Sueldo aseador (rango) */}
          <div className="af-field af-field--wide">
            <label className="af-label">Sueldo aseador</label>
            <div className="af-range-inputs">
              <InputNumber
                min={0}
                controls={false}
                placeholder="Mín"
                value={filterValues.sueldoAseador[0] ?? undefined}
                onChange={(val) =>
                  setFilterValues((f) => ({
                    ...f,
                    sueldoAseador: [val ?? null, f.sueldoAseador[1]],
                  }))
                }
                formatter={clpFormatter}
                parser={clpParser}
              />
              <span className="af-range-sep">–</span>
              <InputNumber
                min={0}
                controls={false}
                placeholder="Máx"
                value={filterValues.sueldoAseador[1] ?? undefined}
                onChange={(val) =>
                  setFilterValues((f) => ({
                    ...f,
                    sueldoAseador: [f.sueldoAseador[0], val ?? null],
                  }))
                }
                formatter={clpFormatter}
                parser={clpParser}
              />
            </div>
          </div>

          {/* Costo limpieza (rango, solo admin) */}
          {showCostoLimpieza && (
            <div className="af-field af-field--wide">
              <label className="af-label">Costo limpieza</label>
              <div className="af-range-inputs">
                <InputNumber
                  min={0}
                  controls={false}
                  placeholder="Mín"
                  value={filterValues.costoLimpieza[0] ?? undefined}
                  onChange={(val) =>
                    setFilterValues((f) => ({
                      ...f,
                      costoLimpieza: [val ?? null, f.costoLimpieza[1]],
                    }))
                  }
                  formatter={clpFormatter}
                  parser={clpParser}
                />
                <span className="af-range-sep">–</span>
                <InputNumber
                  min={0}
                  controls={false}
                  placeholder="Máx"
                  value={filterValues.costoLimpieza[1] ?? undefined}
                  onChange={(val) =>
                    setFilterValues((f) => ({
                      ...f,
                      costoLimpieza: [f.costoLimpieza[0], val ?? null],
                    }))
                  }
                  formatter={clpFormatter}
                  parser={clpParser}
                />
              </div>
            </div>
          )}

          {/* Urgencia */}
          <div className="af-field">
            <label className="af-label">Urgencia</label>
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder="Todas"
              options={[
                { value: "am", label: "AM" },
                { value: "pm", label: "PM" },
                // El ENUM de la DB es "critico 11:00" (con espacio) — con el
                // value corto "critico" el filtro nunca matcheaba (aud. 2026-07-13).
                { value: "critico 11:00", label: "Crítico 11:00" },
                { value: "11:00", label: "11:00" },
              ]}
              value={filterValues.urgencia}
              onChange={(val) =>
                setFilterValues((f) => ({ ...f, urgencia: val }))
              }
              filterSort={(a, b) => {
                const aSel = filterValues.urgencia.includes(a.value as string);
                const bSel = filterValues.urgencia.includes(b.value as string);
                if (aSel !== bSel) return aSel ? -1 : 1;
                return (a.label as string).localeCompare(b.label as string);
              }}
            />
          </div>
        </div>
      </Panel>
    </Collapse>
  );
};

export default AseoFilterBar;
